import { useState, useEffect } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { ClientSelector } from './ClientSelector'
import {
  Calendar,
  Clock,
  User,
  Mail,
  Building2,
  Pencil,
  Trash2,
  XCircle,
  Save,
  X,
  Plus,
  Users,
} from 'lucide-react'
import type { RendezVous } from '@/services/agenda'
import {
  rendezVousStatutLabels,
  rendezVousStatutColors,
} from '@/types/agenda'

interface SelectedClient {
  id: string
  email: string
  full_name: string | null
  company: string | null
}

interface AppointmentDetailModalProps {
  isOpen: boolean
  onClose: () => void
  rdv: RendezVous | null
  onUpdate: (id: string, updates: {
    client_id?: string
    date_heure?: string
    description?: string | null
    participants?: string[]
  }) => Promise<void>
  onCancel: (id: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
  isUpdating?: boolean
  isCancelling?: boolean
  isDeleting?: boolean
}

function formatDateTimeFr(isoString: string): string {
  return new Date(isoString).toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function toDatetimeLocalValue(isoString: string): string {
  const d = new Date(isoString)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function AppointmentDetailModal({
  isOpen,
  onClose,
  rdv,
  onUpdate,
  onCancel,
  onDelete,
  isUpdating,
  isCancelling,
  isDeleting,
}: AppointmentDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Edit state
  const [editDescription, setEditDescription] = useState('')
  const [editDateHeure, setEditDateHeure] = useState('')
  const [editParticipants, setEditParticipants] = useState<string[]>([])
  const [newParticipantEmail, setNewParticipantEmail] = useState('')
  const [participantError, setParticipantError] = useState('')
  const [selectedClient, setSelectedClient] = useState<SelectedClient | null>(null)
  const [clientChanged, setClientChanged] = useState(false)

  // Sync edit state when rdv changes or entering edit mode
  useEffect(() => {
    if (rdv) {
      setEditDescription(rdv.description || '')
      setEditDateHeure(toDatetimeLocalValue(rdv.date_heure))
      setEditParticipants(rdv.participants || [])
      setSelectedClient(rdv.client ? {
        id: rdv.client.id,
        email: rdv.client.email,
        full_name: rdv.client.full_name,
        company: rdv.client.company || null,
      } : null)
      setClientChanged(false)
    }
  }, [rdv, isEditing])

  // Reset state on close
  const handleClose = () => {
    setIsEditing(false)
    setConfirmDelete(false)
    setNewParticipantEmail('')
    setParticipantError('')
    onClose()
  }

  const handleStartEdit = () => {
    setIsEditing(true)
    setConfirmDelete(false)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    if (rdv) {
      setEditDescription(rdv.description || '')
      setEditDateHeure(toDatetimeLocalValue(rdv.date_heure))
      setEditParticipants(rdv.participants || [])
      setSelectedClient(rdv.client ? {
        id: rdv.client.id,
        email: rdv.client.email,
        full_name: rdv.client.full_name,
        company: rdv.client.company || null,
      } : null)
      setClientChanged(false)
    }
  }

  const handleSave = async () => {
    if (!rdv) return

    const updates: {
      client_id?: string
      date_heure?: string
      description?: string | null
      participants?: string[]
    } = {}

    // Check what changed
    const newDateHeure = new Date(editDateHeure).toISOString()
    if (newDateHeure !== rdv.date_heure) {
      updates.date_heure = newDateHeure
    }

    const newDesc = editDescription.trim() || null
    if (newDesc !== (rdv.description || null)) {
      updates.description = newDesc
    }

    if (clientChanged && selectedClient && selectedClient.id !== rdv.client_id) {
      updates.client_id = selectedClient.id
    }

    const origParticipants = rdv.participants || []
    if (
      editParticipants.length !== origParticipants.length ||
      editParticipants.some((p, i) => p !== origParticipants[i])
    ) {
      updates.participants = editParticipants
    }

    if (Object.keys(updates).length === 0) {
      setIsEditing(false)
      return
    }

    await onUpdate(rdv.id, updates)
    setIsEditing(false)
  }

  const handleAddParticipant = () => {
    const email = newParticipantEmail.trim().toLowerCase()
    setParticipantError('')

    if (!email) return

    if (!isValidEmail(email)) {
      setParticipantError('Email invalide')
      return
    }

    if (editParticipants.includes(email)) {
      setParticipantError('Email deja ajouté')
      return
    }

    setEditParticipants([...editParticipants, email])
    setNewParticipantEmail('')
  }

  const handleRemoveParticipant = (email: string) => {
    setEditParticipants(editParticipants.filter(p => p !== email))
  }

  const handleParticipantKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddParticipant()
    }
  }

  const handleAnnuler = async () => {
    if (!rdv) return
    await onCancel(rdv.id)
    handleClose()
  }

  const handleDelete = async () => {
    if (!rdv) return
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    await onDelete(rdv.id)
    handleClose()
  }

  if (!rdv) return null

  const statutColor = rendezVousStatutColors[rdv.statut]
  const isCancelled = rdv.statut === 'annule'
  const isTermine = rdv.statut === 'termine'
  const canEdit = !isCancelled && !isTermine
  const canCancel = rdv.statut === 'en_attente' || rdv.statut === 'confirme'

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Modifier le rendez-vous' : 'Détail du rendez-vous'}
      size="lg"
    >
      {!isEditing ? (
        /* ====== MODE LECTURE ====== */
        <div className="space-y-5">
          {/* Statut */}
          <div className="flex items-center gap-3">
            <Badge variant={statutColor} size="md">
              {rendezVousStatutLabels[rdv.statut]}
            </Badge>
          </div>

          {/* Date/heure */}
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-brown-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-brown-700">Date et heure</p>
              <p className="text-brown-900 capitalize">{formatDateTimeFr(rdv.date_heure)}</p>
            </div>
          </div>

          {/* Client / Prospect / Guest */}
          {(() => {
            const prospectContact = rdv.prospect?.contact_name || null
            const prospectCompany = rdv.prospect?.nom_commercial || rdv.prospect?.raison_sociale || null
            const displayName =
              rdv.client?.full_name
              || prospectContact
              || prospectCompany
              || rdv.guest_name
              || rdv.client?.email
              || rdv.prospect?.email
              || rdv.guest_email
              || 'Sans nom'
            const displayCompany =
              rdv.client?.company
              || (prospectContact ? prospectCompany : null)
            const displayEmail = rdv.client?.email || rdv.prospect?.email || rdv.guest_email || null
            const isProspect = !!rdv.prospect_id && !rdv.client_id
            return (
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-brown-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-brown-700">
                    {isProspect ? 'Prospect' : 'Client'}
                  </p>
                  <p className="text-brown-900">{displayName}</p>
                  {displayCompany && (
                    <p className="text-sm text-brown-500 flex items-center gap-1 mt-0.5">
                      <Building2 className="w-3 h-3" />
                      {displayCompany}
                    </p>
                  )}
                  {displayEmail && (
                    <p className="text-sm text-brown-500 flex items-center gap-1 mt-0.5">
                      <Mail className="w-3 h-3" />
                      {displayEmail}
                    </p>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Description */}
          {rdv.description && (
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-brown-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-brown-700">Description</p>
                <p className="text-brown-900 whitespace-pre-wrap">{rdv.description}</p>
              </div>
            </div>
          )}

          {/* Participants */}
          {rdv.participants && rdv.participants.length > 0 && (
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-brown-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-brown-700">Participants</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {rdv.participants.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-beige-100 text-brown-700 rounded-full text-sm"
                    >
                      <Mail className="w-3 h-3" />
                      {email}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-3 border-t border-beige-200">
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleStartEdit}
                leftIcon={<Pencil className="w-4 h-4" />}
              >
                Modifier
              </Button>
            )}
            {canCancel && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAnnuler}
                isLoading={isCancelling}
                leftIcon={<XCircle className="w-4 h-4" />}
                className="text-gold-700 border-gold-300 hover:bg-gold-50"
              >
                Annuler le RDV
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              isLoading={isDeleting}
              leftIcon={<Trash2 className="w-4 h-4" />}
              className="text-red-600 border-red-200 hover:bg-red-50 ml-auto"
            >
              {confirmDelete ? 'Confirmer la suppression' : 'Supprimer'}
            </Button>
          </div>
        </div>
      ) : (
        /* ====== MODE EDITION ====== */
        <div className="space-y-5">
          {/* Client */}
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-2">
              Client
            </label>
            <ClientSelector
              onSelect={(client) => {
                setSelectedClient(client)
                setClientChanged(true)
              }}
              onSwitchToManual={() => {}}
              selectedClient={selectedClient}
              onClear={() => {
                setSelectedClient(null)
                setClientChanged(true)
              }}
            />
          </div>

          {/* Date/heure */}
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Date et heure
            </label>
            <Input
              type="datetime-local"
              value={editDateHeure}
              onChange={(e) => setEditDateHeure(e.target.value)}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Description / Motif
            </label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 text-sm"
              rows={3}
              placeholder="Motif du rendez-vous..."
            />
          </div>

          {/* Participants */}
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-2">
              Participants (emails additionnels)
            </label>

            {/* Current participants */}
            {editParticipants.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {editParticipants.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-beige-100 text-brown-700 rounded-full text-sm"
                  >
                    <Mail className="w-3 h-3" />
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveParticipant(email)}
                      className="ml-1 p-0.5 hover:bg-beige-200 rounded-full"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add participant */}
            <div className="flex gap-2">
              <Input
                type="email"
                value={newParticipantEmail}
                onChange={(e) => {
                  setNewParticipantEmail(e.target.value)
                  setParticipantError('')
                }}
                onKeyDown={handleParticipantKeyDown}
                placeholder="email@exemple.com"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddParticipant}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Ajouter
              </Button>
            </div>
            {participantError && (
              <p className="text-sm text-red-500 mt-1">{participantError}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-3 border-t border-beige-200">
            <Button
              variant="outline"
              onClick={handleCancelEdit}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              isLoading={isUpdating}
              leftIcon={<Save className="w-4 h-4" />}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
